/**
 * ErrorLogService.ts — Global error capture & batched reporting
 *
 * Captures:
 *   - Vue app.config.errorHandler (component errors)
 *   - window 'error' event (uncaught runtime errors)
 *   - window 'unhandledrejection' (unhandled Promise rejections)
 *
 * Behaviour:
 *   - Errors are queued in memory (max 100)
 *   - Batched and sent every 10 seconds via REST POST /api/errors
 *   - Falls back to IndexedDB buffer if the server is unreachable
 *   - Rate-limited to max 10 unique errors per minute per client
 *   - Ignores ResizeObserver/chunk noise per message, and everything whose
 *     stack liegt komplett in fremdem Code (Erweiterungen, injizierte Skripte)
 *
 * Usage (in main.ts):
 *   import ErrorLogService from '@/core/services/ErrorLogService'
 *   ErrorLogService.init(app)
 */

import type { App, ComponentPublicInstance } from 'vue'
import { httpsSafeUrl } from '@/core/helpers/httpsSafeUrl'
import { Capacitor } from '@capacitor/core'

// ── Types ────────────────────────────────────────────────────────────────────

interface ErrorEntry {
  message: string
  stack: string
  component: string
  url: string
  userAgent: string
  context: Record<string, unknown>
  timestamp: string
}

// ── Configuration ────────────────────────────────────────────────────────────

const MAX_QUEUE_SIZE = 100
const FLUSH_INTERVAL_MS = 10_000       // 10 seconds
const RATE_LIMIT_PER_MINUTE = 10
const RATE_LIMIT_WINDOW_MS = 60_000

// Meldungen, die als Rauschen gelten. Geprueft wird gegen die MESSAGE — die
// Protokolle fremder Skripte standen frueher ebenfalls in dieser Liste und
// konnten dort nie greifen: `chrome-extension://` taucht ausschliesslich im
// Stack auf, nie in der Fehlermeldung. Sie sind nach FOREIGN_FRAME gewandert.
const IGNORE_MESSAGE_PATTERNS = [
  /^Script error\.?$/i,                          // cross-origin script errors
  /ResizeObserver loop/i,                        // benign browser warning
  /Loading chunk \d+ failed/i,                   // lazy-load chunk failures (handled by router)
  /Failed to fetch dynamically imported module/i,
  /Unexpected token '<'/i,                       // HTML returned instead of JS (deploy in progress)
]

// Ein Stack-Frame, der NICHT aus unserem Bundle stammt.
//
// Zwei Sorten:
//  - Erweiterungen melden sich mit ihrem eigenen Protokoll.
//  - Code, der ohne Quell-URL ausgewertet wurde, erscheint als `<anonymous>`
//    (Chrome) bzw. `> eval` (Firefox). So sieht die Live-Metrics-Einblendung
//    der DevTools aus, ebenso die Web-Vitals-Erweiterung: ein einzeiliges
//    Bundle, das in die Seite injiziert wird. Unser eigener Code hat immer
//    eine `https://`-URL im Frame, auch minifiziert.
const FOREIGN_FRAME = /(?:chrome|moz|safari(?:-web)?|ms-browser)-extension:\/\/|<anonymous>|> eval/

/**
 * Stammt der Fehler vollstaendig aus fremdem Code?
 *
 * Bewusst „jeder Frame fremd" statt „ein Frame fremd": ruft eine Erweiterung in
 * unseren Code hinein und der stirbt, ist das unser Fehler und gehoert gemeldet.
 * Ein Stack ohne erkennbare Frames (geworfene Strings, leerer Stack) faellt
 * durch — lieber eine Zeile zu viel als eine echte verloren.
 *
 * Als Frame zaehlt auch eine nackte `datei:zeile:spalte`-Zeile: fehlt bei einem
 * `window.onerror` das Error-Objekt, bauen wir den Stack genau so zusammen.
 */
export function isForeignStack (stack: string): boolean {
  const frames = stack.split('\n').filter(line => /(?:^|\s)at\s|@|:\d+:\d+/.test(line))
  if (frames.length === 0) return false
  return frames.every(line => FOREIGN_FRAME.test(line))
}

// ── State ────────────────────────────────────────────────────────────────────

let queue: ErrorEntry[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null
let rateLimitCount = 0
let rateLimitResetTimer: ReturnType<typeof setTimeout> | null = null
let companyId = ''
let isInitialised = false

// ── Server URL resolution ────────────────────────────────────────────────────
// Mirror the logic from server.worker.ts getBaseURL()

function getBaseURL(): string {
  const h = location.hostname
  if (h === 'localhost' || h.startsWith('192.168') || h === 'studio.mycrogym.dev') {
    // httpsSafeUrl: Safari blocks http://localhost from HTTPS pages (mixed
    // content) — route through the Vite /api proxy instead.
    return httpsSafeUrl('http://localhost:8081')
  }
  if (h === 'studio.atlas.max-min.de') return 'https://api.atlas.max-min.de/studio'
  if (h === 'studio.mycrogym.com' || h === 'studio-stage.mycrogym.com') {
    return 'https://api.mycrogym.com/studio'
  }
  return httpsSafeUrl('http://localhost:8081')
}

// ── Core methods ─────────────────────────────────────────────────────────────

export function shouldIgnore(message: string, stack = ''): boolean {
  if (IGNORE_MESSAGE_PATTERNS.some(pattern => pattern.test(message))) return true
  return isForeignStack(stack)
}

function isRateLimited(): boolean {
  if (rateLimitCount >= RATE_LIMIT_PER_MINUTE) return true
  rateLimitCount++
  if (!rateLimitResetTimer) {
    rateLimitResetTimer = setTimeout(() => {
      rateLimitCount = 0
      rateLimitResetTimer = null
    }, RATE_LIMIT_WINDOW_MS)
  }
  return false
}

function enqueue(entry: ErrorEntry): void {
  if (queue.length >= MAX_QUEUE_SIZE) {
    // Drop oldest to make room
    queue.shift()
  }
  queue.push(entry)
}

/**
 * Was der User-Agent NICHT hergibt.
 *
 * Zwei Luecken, die beide in diesem Haus vorkommen:
 *  - Die Capacitor-Huellen (iOS, macOS) senden einen ganz normalen Safari-UA.
 *    Ob die App oder der Browser laeuft, weiss nur der Client.
 *  - iPadOS-Safari meldet sich im Standardmodus als macOS. `maxTouchPoints` ist
 *    die Stelle, an der iPad und Mac auseinandergehen: beide senden denselben
 *    UA, nur das iPad meldet mehr als einen Touchpunkt.
 *
 * Ausgewertet wird das serverseitig in errorLogService.deviceKey(). Hier stehen
 * bewusst nur Rohwerte — die Einteilung in Eimer gehoert an EINE Stelle, sonst
 * driften Client und Server auseinander und alte Clients liefern alte Namen.
 *
 * Kein Fingerprinting: nichts davon identifiziert ein Geraet, es sind drei
 * grobe Merkmale, die es in jeder Browser-Klasse millionenfach gibt.
 */
function describeDevice(): Record<string, unknown> {
  try {
    return {
      platform: Capacitor.getPlatform(),          // 'ios' | 'electron' | 'web'
      native: Capacitor.isNativePlatform(),
      touch: navigator.maxTouchPoints ?? 0,
    }
  } catch {
    // Capacitor fehlt im reinen Web-Build nicht, aber ein Fehler HIER waere
    // besonders bitter: er entstuende im Melden eines anderen Fehlers.
    return {}
  }
}

function createEntry(
  message: string,
  stack: string,
  component: string,
  extraContext?: Record<string, unknown>
): ErrorEntry {
  return {
    message: (message || 'Unknown error').slice(0, 4096),
    stack: (stack || '').slice(0, 8192),
    component: (component || '').slice(0, 255),
    url: location.href,
    userAgent: navigator.userAgent,
    context: {
      route: location.pathname + location.hash,
      device: describeDevice(),
      ...extraContext,
    },
    timestamp: new Date().toISOString(),
  }
}

async function flush(): Promise<void> {
  if (queue.length === 0 || !companyId) return

  const batch = queue.splice(0, 50) // Take up to 50
  const url = `${getBaseURL()}/api/errors`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyid: companyId, errors: batch }),
    })

    if (!response.ok) {
      console.warn('[ErrorLogService] Server returned', response.status, '— re-queuing')
      queue.unshift(...batch) // Put them back
    }
  } catch (err) {
    // Server unreachable — re-queue for next attempt
    console.warn('[ErrorLogService] Flush failed — will retry:', (err as Error).message)
    queue.unshift(...batch)
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

const ErrorLogService = {
  /**
   * Initialise error capturing on the Vue app.
   * Call once in main.ts after createApp().
   */
  init(app: App): void {
    if (isInitialised) return
    isInitialised = true

    // Vue component error handler
    app.config.errorHandler = (
      err: unknown,
      instance: ComponentPublicInstance | null,
      info: string
    ) => {
      const error = err instanceof Error ? err : new Error(String(err))
      const componentName = instance?.$options?.name || instance?.$options?.__name || ''

      if (shouldIgnore(error.message, error.stack || '') || isRateLimited()) return

      // Still log to console for dev visibility
      console.error(`[Vue Error] ${info}:`, error)

      enqueue(createEntry(error.message, error.stack || '', componentName, {
        vueInfo: info,
        lifecycleHook: info,
      }))
    }

    // Uncaught runtime errors
    window.addEventListener('error', (event: ErrorEvent) => {
      const message = event.message || String(event.error)
      const stack = event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`
      if (shouldIgnore(message, stack) || isRateLimited()) return

      enqueue(createEntry(message, stack, '', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }))
    })

    // Unhandled Promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason
      const message = reason instanceof Error ? reason.message : String(reason)
      const stack = reason instanceof Error ? (reason.stack || '') : ''
      if (shouldIgnore(message, stack) || isRateLimited()) return

      enqueue(createEntry(`Unhandled Promise Rejection: ${message}`, stack, '', {
        type: 'unhandledrejection',
      }))
    })

    // Start flush timer
    flushTimer = setInterval(flush, FLUSH_INTERVAL_MS)

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      if (queue.length > 0 && companyId) {
        // Use sendBeacon for reliable delivery on page close
        const url = `${getBaseURL()}/api/errors`
        const body = JSON.stringify({ companyid: companyId, errors: queue.splice(0, 50) })
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
      }
    })

    console.log('[ErrorLogService] Initialised — capturing Vue, window, and Promise errors')
  },

  /**
   * Set the active company ID. Call when company is known (after auth).
   */
  setCompanyId(id: string): void {
    companyId = id
  },

  /**
   * Manually report an error (e.g. from a try/catch block).
   */
  report(message: string, extra?: { stack?: string; component?: string; context?: Record<string, unknown> }): void {
    if (shouldIgnore(message, extra?.stack || '') || isRateLimited()) return
    enqueue(createEntry(message, extra?.stack || '', extra?.component || '', extra?.context))
  },

  /**
   * Force an immediate flush (e.g. before logout).
   */
  async flush(): Promise<void> {
    await flush()
  },

  /**
   * Tear down listeners and timers.
   */
  destroy(): void {
    if (flushTimer) {
      clearInterval(flushTimer)
      flushTimer = null
    }
    if (rateLimitResetTimer) {
      clearTimeout(rateLimitResetTimer)
      rateLimitResetTimer = null
    }
    isInitialised = false
    queue = []
  },
}

export default ErrorLogService
